import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Calendar, User, Phone, ArrowUpRight, Search } from "lucide-react";
import { api } from "@/lib/api";
import { StatusPill } from "./OpsDashboard";

const STATUSES = ["", "pending", "scheduled", "in_progress", "completed", "cancelled"];

export default function OpsOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } catch {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = orders.filter((o) => {
    if (filter && o.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${o.customer?.full_name || ""} ${o.customer?.phone || ""} ${o.service || ""} ${o.assigned_to || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const createBlank = async () => {
    try {
      const { data } = await api.post("/orders", {
        customer: { full_name: "Nuevo cliente", phone: "", email: "", address: "" },
        service: "Moving Services",
        status: "pending",
      });
      navigate(`/admin/ops/orders/${data.id}`);
    } catch {
      toast.error("No se pudo crear");
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Trabajo</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Pedidos</h1>
        </div>
        <button
          onClick={createBlank}
          data-testid="order-create"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold bg-[#D4AF37] text-[#0A0A0A] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all"
        >
          <Plus className="w-4 h-4" /> Nuevo pedido
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por cliente, teléfono, servicio o asignado…"
            data-testid="orders-search"
            className="input-dark pl-10"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="orders-filter"
          className="input-dark sm:w-56 cursor-pointer"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s === "" ? "Todos los estados" : s.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center text-white/55">
          {orders.length === 0 ? "Aún no hay pedidos. Crea uno o conviértelo desde una solicitud." : "Ningún pedido coincide con el filtro."}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <Link
              key={o.id}
              to={`/admin/ops/orders/${o.id}`}
              data-testid={`order-row-${o.id}`}
              className="block bg-white/[0.04] border border-white/10 rounded-2xl p-5 hover:border-[#D4AF37]/50 transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-base sm:text-lg font-bold truncate">{o.customer?.full_name || "Sin nombre"}</h3>
                    <StatusPill value={o.status} />
                    {o.invoice_id && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold">
                        Facturado
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55">
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3 text-[#D4AF37]" /> {o.service}</span>
                    {o.customer?.phone && (
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3 text-[#D4AF37]" /> {o.customer.phone}</span>
                    )}
                    {o.scheduled_at && (
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-[#D4AF37]" /> {new Date(o.scheduled_at).toLocaleString()}</span>
                    )}
                    {o.assigned_to && (
                      <span className="inline-flex items-center gap-1">👤 {o.assigned_to}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {o.price > 0 && (
                    <div className="font-display text-lg font-bold text-[#D4AF37]">
                      ${Number(o.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  )}
                  <ArrowUpRight className="w-4 h-4 text-white/30" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
